import { useEffect, useState } from 'react'

/**
 * useDebouncedValue — returns a debounced copy of `value` that only updates after
 * `ms` of quiet time. Use for search inputs / filter fields so downstream effects
 * (fetches, expensive renders) don't fire on every keystroke.
 *
 *   const [q, setQ] = useState('')
 *   const debouncedQ = useDebouncedValue(q, 250)
 *   useEffect(() => { fetchResults(debouncedQ) }, [debouncedQ])
 *
 * The pending timer is cleared on every change and on unmount, so the latest value
 * always wins and no update fires after the component is gone.
 */
export function useDebouncedValue<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), Math.max(0, ms))
    return () => clearTimeout(timer)
  }, [value, ms])

  return debounced
}
